'use client';

import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Button } from '@/components/ui/forms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { generateId } from '../../utils';
import type { ResumeLinkType } from '@/lib/resume/types';

const LINK_TYPES: { value: ResumeLinkType; label: string }[] = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'github', label: 'GitHub' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'custom', label: 'Custom' },
];

export default function LinksSection() {
  const { resume, addLink, updateLink, removeLink } = useResumeBuilderStore();

  if (!resume) return null;

  const { links } = resume.content;

  const handleAdd = () => {
    addLink({
      id: generateId(),
      type: 'linkedin',
      url: '',
    });
  };

  return (
    <div className="space-y-4">
      {links.length === 0 ? (
        <p className="text-body text-muted-foreground">
          Add links to your LinkedIn, portfolio, GitHub, or other professional profiles.
        </p>
      ) : (
        <div className="space-y-4">
          {links.map((link) => (
            <div key={link.id} className="flex gap-2">
              <div className="w-32">
                <Select
                  value={link.type}
                  onValueChange={(value) => updateLink(link.id, { type: value as ResumeLinkType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINK_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Input
                  type="url"
                  value={link.url}
                  onChange={(e) => updateLink(link.id, { url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              {link.type === 'custom' && (
                <div className="w-32">
                  <Input
                    value={link.label || ''}
                    onChange={(e) => updateLink(link.id, { label: e.target.value })}
                    placeholder="Label"
                  />
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeLink(link.id)}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
        <Plus className="mr-2 h-4 w-4" />
        Add Link
      </Button>
    </div>
  );
}
